import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Target, CheckCircle, XCircle } from 'lucide-react';
export function ManipulationResistancePage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-1039">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-1040">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-1041">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-1042">
            <span className="w-8 h-px bg-teal" data-id="element-1043"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-1044">
              Capability
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-1045"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-1046">
            Manipulation Resistance
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-1047">
            At SyNeural, manipulation resistance refers to the ability to detect
            and resist propaganda, emotional framing, rhetorical deception,
            identity-based pressure, narrative capture, and other forms of
            influence designed to bypass clear reasoning.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1048">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1049">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-1050">
            It is not about distrusting everything. It is about being able to
            notice when someone is trying to move judgment through distortion,
            fear, tribal loyalty, status pressure, or selective framing rather
            than through sound reasoning and evidence.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1051">
            This capability matters because modern individuals and institutions
            operate in environments saturated with persuasion. Information
            systems are increasingly optimized not for clarity, but for
            attention, reaction, compliance, and emotional intensity. Without
            manipulation resistance, people can become highly vulnerable to
            narratives that feel compelling while degrading judgment over time.
          </p>
        </motion.div>
      </section>

      {/* Why It Matters */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-1052">
        <div className="max-w-4xl mx-auto" data-id="element-1053">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1054">
            <div className="flex items-center gap-4 mb-6" data-id="element-1055">
              <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-1056">
                <Target size={24} data-id="element-1057" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1058">
                Why It Matters
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1059">
              Manipulation resistance protects independent thought. It helps
              people preserve perspective, question emotional framing, and avoid
              being absorbed into narratives that distort reality or narrow
              judgment. Weak manipulation resistance can make individuals,
              communities, and institutions more vulnerable to misinformation,
              moral panic, ideological capture, group pressure, and poor
              decision-making under social stress.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Strong Manipulation Resistance Looks Like */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1060">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1061">
          <div className="flex items-center gap-4 mb-6" data-id="element-1062">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-1063">
              <CheckCircle size={24} data-id="element-1064" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1065">
              What Strong Manipulation Resistance Looks Like
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1066">
            People with stronger manipulation resistance tend to notice loaded
            language, emotional shortcuts, selective evidence, false urgency,
            simplistic enemies, and attempts to exploit identity, fear, or
            grievance. They are more likely to pause, verify, compare
            perspectives, and ask whether a message is informing them or merely
            trying to move them. This does not make them cynical. It makes them
            harder to capture.
          </p>
        </motion.div>
      </section>

      {/* What Weak Manipulation Resistance Looks Like */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-line" data-id="element-1067">
        <div className="max-w-4xl mx-auto" data-id="element-1068">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1069">
            <div className="flex items-center gap-4 mb-6" data-id="element-1070">
              <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-1071">
                <XCircle size={24} data-id="element-1072" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1073">
                What Weak Manipulation Resistance Looks Like
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1074">
              Weak manipulation resistance often shows up as susceptibility to
              rumor, strong reaction to emotional narratives, overreliance on
              group cues, and difficulty separating persuasion from truth.
              People weak in this area may accept claims because they are
              repeated often, socially rewarded, emotionally satisfying, or
              aligned with a preexisting identity. In civic and institutional
              environments, this can produce instability, poor judgment, and
              fractured trust.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How It Can Be Strengthened */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1075">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1076">
          <div className="flex items-center gap-4 mb-6" data-id="element-1077">
            <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-1078">
              <Shield size={24} data-id="element-1079" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1080">
              How It Can Be Strengthened
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1081">
            Manipulation resistance can be improved through media literacy,
            propaganda analysis, rhetoric study, logic training, long-form
            reading, debate, and historical awareness of mass persuasion. It
            also improves when people become more conscious of their own
            emotional triggers and identity vulnerabilities. In the SyNeural
            framework, this capability is essential for civic resilience,
            institutional stability, and trustworthy leadership.
          </p>
        </motion.div>
      </section>
    </div>;
}